import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import functions from "../../util/functions";

/**
 * Sends a signed-out user to the login page.
 *
 * Whether someone is signed in used to be decided once, when the app started.
 * After that the app carried on regardless: sign out in another tab, or clear
 * site data, and this one kept rendering pages that quietly failed every
 * request behind them — which is how a dead session came to look like a broken
 * system.
 *
 * Three things are watched, because a session can end in three ways:
 *   • a navigation, so a signed-out user cannot walk into another page;
 *   • the token going missing in another tab, via the storage event;
 *   • the tab being brought back to the front after time away.
 *
 * The login page itself is left alone, or leaving would be a loop.
 */
export default function SessionWatch() {
  const location = useLocation();

  useEffect(() => {
    // The pages you are meant to reach without a session. Sending someone
    // resetting a forgotten password back to the sign-in form they cannot use
    // would be its own small trap.
    const open = ["/login", "/forgot-password"].includes(location.pathname);

    const check = () => {
      if (!open && !functions.hasSession()) functions.endSession();
    };

    check();

    // Fires in the *other* tabs when one of them signs out.
    const onStorage = (e) => {
      if (!e.key || e.key === "projectm@user") check();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };

    window.addEventListener("storage", onStorage);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [location.pathname]);

  return null;
}
